import { useRequest } from '@umijs/max';
import {
  Descriptions,
  Flex,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import React, { useMemo, useState } from 'react';
import RobotSampleImage from '@/assets/robot-sample.png';
import RobotSampleOutlinedImage from '@/assets/robot-sample-outlined.svg';
import {
  getRobotDetail,
  ROBOT_BATTERY_PARTS,
  ROBOT_JOINTS_MOTOR_NORMAL_VALUES,
  ROBOT_PARTS,
  ROBOT_TEMPERATURE_STATUS,
} from '@/services';
import DynamicBattery from '../Battery';
import IconFont from '../IconFont';

import styles from './index.less';

const { Text, Title } = Typography;

interface RobotStatusCardProps {
  // serialNumber?: string;
  width?: number;
  data?: API.ROBOT.Robot;
  status?: API.ROBOT.DeviceStatus;
  motion?: API.ROBOT.DeviceMotion;
  patrol?: API.ROBOT.DeviceTask;
}

const RobotStatusCard: React.FC<RobotStatusCardProps> = ({
  data,
  status,
  motion,
  patrol,
  width = 480,
  // serialNumber,
}) => {
  // const { data, loading } = useRequest(
  //   () =>
  //     serialNumber ? getRobotDetail({ serialNumber }) : Promise.resolve(robot),
  //   {
  //     refreshDeps: [serialNumber],
  //     formatResult: (res) => res,
  //   }
  // );

  const [temperatureType, setTemperatureType] =
    useState<API.ROBOT.TemperatureType>('Motor');
  const onTemperatureTypeChange = (key: string) => {
    setTemperatureType(key as API.ROBOT.TemperatureType);
  };

  const speed = useMemo(() => {
    if (
      motion &&
      (motion.motionStatus.linearX || motion.motionStatus.linearY)
    ) {
      return Math.sqrt(
        motion.motionStatus.linearX ** 2 + motion.motionStatus.linearY ** 2,
      ).toFixed(2);
    }
    return 0;
  }, [motion]);

  const temperatureLegend = (
    <Flex align="center" gap={16}>
      {Object.values(ROBOT_TEMPERATURE_STATUS).map((status) => (
        <Flex
          key={status.label}
          align="center"
          gap={5}
          style={{ margin: '-4px 0 4px' }}
        >
          <span
            className={styles.dot}
            style={{ backgroundColor: status.color }}
          ></span>
          <Text>{status.label}</Text>
        </Flex>
      ))}
    </Flex>
  );

  const getTemperatureStatus = (temperature: number) => {
    let color = ROBOT_TEMPERATURE_STATUS[0].color;
    Object.entries(ROBOT_TEMPERATURE_STATUS).forEach(([key, value]) => {
      if (temperature > Number(key)) {
        color = value.color;
      }
    });
    return color;
  };

  const getJointMotorStatus = (joint: keyof API.ROBOT.MotorStatus) => {
    const [min, max] = ROBOT_JOINTS_MOTOR_NORMAL_VALUES[joint];
    if (!motion?.motorStatus || motion?.motorStatus[joint] === undefined)
      return null;
    const rad = motion.motorStatus?.[joint];
    const deg = (rad * 180) / Math.PI;
    const value = joint.includes('Wheel') ? rad : deg;
    const unit = joint.includes('Wheel') ? ' rad/s' : '°';
    if (Math.abs(value) >= min && Math.abs(value) <= max) {
      return {
        label: '正常',
        color: 'success',
        value: value.toFixed(2),
        unit,
      };
    } else {
      return {
        label: '异常',
        color: 'warning',
        value: value.toFixed(2),
        unit,
      };
    }
  };

  return data ? (
    <Flex className={styles['robot-card']} vertical gap={24} style={{ width }}>
      <Flex
        className={styles.header}
        align="center"
        justify="space-between"
        gap={16}
      >
        <Title level={4} className={styles['robot-name']}>
          {data.robotName}
          {!!data.hasArm && (
            <IconFont type="icon-arm" className={styles['robot-arm']} />
          )}
        </Title>
        {/* <Tag color={ROBOT_STATUS_MAP[data.status].color} style={{}}>
          {ROBOT_STATUS_MAP[data.status].label}
        </Tag> */}
        <Flex
          className={styles['robot-statistics']}
          align="center"
          gap={24}
          style={{ marginBlockStart: 4 }}
        >
          {motion && speed ? <Text>速度：{speed}m/s</Text> : null}
          {status ? (
            <>
              <DynamicBattery
                level={status.batteryStatus.batteryLevelLeft || 0}
                size={30}
                showNumber
                isCharging={status.batteryStatus.chargeLeft}
              />
              <DynamicBattery
                level={status.batteryStatus.batteryLevelRight || 0}
                size={30}
                showNumber
                isCharging={status.batteryStatus.chargeRight}
                style={{ marginInlineEnd: 10 }}
              />
            </>
          ) : (
            <>
              <DynamicBattery
                level={data.batteryLevel || 0}
                size={30}
                showNumber
                isCharging={!!data.isCharging || data.status === 'charging'}
                style={{ marginInlineEnd: 10 }}
              />
            </>
          )}
        </Flex>
      </Flex>
      {status ? (
        <>
          <div className={styles.section}>
            <Title level={5} className={styles.title}>
              关节温度 ℃
            </Title>
            <Tabs
              onChange={onTemperatureTypeChange}
              className={styles['button-tabs']}
              items={[
                {
                  label: '电机',
                  key: 'Motor',
                },
                {
                  label: '驱动器',
                  key: 'Driver',
                },
              ].map((item) => ({
                ...item,
                children: (
                  <div className={styles['tab-content']}>
                    {temperatureLegend}
                    <img src={RobotSampleImage} alt="机器人" />
                    {ROBOT_PARTS.map((part) =>
                      part.params.map((param) => {
                        const key: keyof API.ROBOT.DeviceTemperature = `${part.key}${param.key}${temperatureType}`;
                        return (
                          <div
                            className={styles['temperature-marker']}
                            key={key}
                            style={{
                              left: param.marker?.x + '%',
                              top: param.marker?.y + '%',
                              backgroundColor: getTemperatureStatus(
                                status.deviceTemperature[key],
                              ),
                            }}
                            title={`${part.label}${param.label}`}
                          >
                            {Math.round(status.deviceTemperature[key])}
                          </div>
                        );
                      }),
                    )}
                    {/* {ROBOT_BATTERY_PARTS.map((part) => (
                      <div
                        className={styles['temperature-marker']}
                        key={part.key}
                        style={{
                          left: part.marker.x + '%',
                          top: part.marker.y + '%',
                          backgroundColor: getTemperatureStatus(
                            status.batteryStatus[part.key],
                          ),
                        }}
                        title={`${part.label}`}
                      >
                        {Math.round(status.batteryStatus[part.key])}
                      </div>
                    ))} */}
                  </div>
                ),
              }))}
            />
          </div>
          {motion && (
            <div className={styles.section}>
              <Title level={5} className={styles.title}>
                关节参数
              </Title>
              <Tabs
                // onChange={onChange}
                className={styles['button-tabs']}
                items={ROBOT_PARTS.map((part) => ({
                  label: part.label,
                  key: part.key,
                  children: (
                    <div className={styles['tab-content']}>
                      <Descriptions
                        column={2}
                        items={part.params.map((param) => {
                          const key: keyof API.ROBOT.MotorStatus = `${part.key}${param.key}`;
                          const motorStatus = getJointMotorStatus(key);
                          return {
                            label: param.label,
                            key: param.key,
                            children: motorStatus ? (
                              <Space>
                                <Text>
                                  {motorStatus.value}
                                  {motorStatus.unit}
                                </Text>
                                {motorStatus ? (
                                  <Tag
                                    bordered={false}
                                    color={motorStatus.color}
                                  >
                                    {motorStatus.label}
                                  </Tag>
                                ) : null}
                              </Space>
                            ) : null,
                          };
                        })}
                      />
                    </div>
                  ),
                }))}
              />
            </div>
          )}
          <div className={styles.section}>
            <Title level={5} className={styles.title}>
              照明灯
            </Title>
            <div className={styles['tab-content']}>
              <Descriptions
                column={2}
                items={[
                  {
                    label: '前照明灯',
                    key: 'front',
                  },
                  {
                    label: '后照明灯',
                    key: 'back',
                  },
                ].map((item) => ({
                  label: item.label,
                  key: item.key,
                  children: (
                    <Flex align="center" gap={5}>
                      <span
                        className={styles.dot}
                        style={{
                          backgroundColor: status?.led.fill.front
                            ? 'var(--ant-color-success)'
                            : 'var(--ant-color-error)',
                        }}
                      ></span>
                      <Text>{status?.led.fill.front ? '开' : '关'}</Text>
                    </Flex>
                  ),
                }))}
              />
            </div>
          </div>
          <div className={styles.section}>
            <Title level={5} className={styles.title}>
              电池状况
            </Title>
            <div className={styles['tab-content']}>
              <Descriptions
                column={2}
                layout="vertical"
                items={[
                  {
                    label: '左侧电池',
                    key: 'Left',
                    children: (
                      <Flex vertical gap={5}>
                        <Flex align="center" gap={5}>
                          电量：
                          <DynamicBattery
                            level={status.batteryStatus.batteryLevelLeft || 0}
                            size={25}
                            showNumber
                            isCharging={status.batteryStatus.chargeLeft}
                          />
                        </Flex>
                        <Text>
                          温度：
                          {status.batteryStatus.batteryTemperatureLeft.toFixed(
                            2,
                          )}{' '}
                          ℃
                        </Text>
                        <Text>电压：{status.batteryStatus.voltageLeft} V</Text>
                      </Flex>
                    ),
                  },
                  {
                    label: '右侧电池',
                    key: 'Right',
                    children: (
                      <Flex vertical gap={5}>
                        <Flex align="center" gap={5}>
                          电量：
                          <DynamicBattery
                            level={status.batteryStatus.batteryLevelRight || 0}
                            size={25}
                            showNumber
                            isCharging={status.batteryStatus.chargeRight}
                          />
                        </Flex>
                        <Text>
                          温度：
                          {status.batteryStatus.batteryTemperatureRight.toFixed(
                            2,
                          )}{' '}
                          ℃
                        </Text>
                        <Text>电压：{status.batteryStatus.voltageRight} V</Text>
                      </Flex>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </>
      ) : null}
    </Flex>
  ) : (
    <Skeleton.Node active style={{ width: 320, height: 28 }} />
  );
};

export default RobotStatusCard;
